import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Star, Trash2 } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlayOverlay } from "@/components/site/Badges";
import { btnSecondary, ConfirmDelete, inputCls } from "@/components/admin/AdminLayout";

const Thumb = ({ m }) => {
  if (m.status === "processing")
    return (
      <div className="grid h-full place-items-center text-center text-sm text-[#FFD23F]" data-testid="media-processing">
        <span><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />Processing…</span>
      </div>
    );
  if (m.status === "failed")
    return (
      <div className="grid h-full place-items-center p-3 text-center text-sm text-[#ff8a80]" data-testid="media-failed">
        <span><AlertTriangle className="mx-auto mb-2 h-6 w-6" />Video couldn't be processed — try a shorter clip</span>
      </div>
    );
  return (
    <>
      <img src={mediaUrl(m.thumb_url)} alt={m.caption || ""} className="h-full w-full object-cover" />
      {m.kind === "video" && <PlayOverlay small />}
    </>
  );
};

export const MediaItem = ({ m, isCover, onChanged, onDeleted, onCover }) => {
  const [caption, setCaption] = useState(m.caption);
  const [busy, setBusy] = useState(false);
  const saveCaption = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/media/${m.id}`, { caption });
      onChanged(data);
      toast.success("Caption saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/media/${m.id}`);
      onDeleted(m.id);
      toast.success("Deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <li className="overflow-hidden rounded-md border border-zinc-800 bg-[#121214]" data-testid={`admin-media-${m.id}`}>
      <div className="relative aspect-[4/3] bg-black">
        <Thumb m={m} />
        {isCover && <span className="absolute left-2 top-2 rounded-sm bg-[#FFD23F] px-2 py-1 text-xs font-bold uppercase text-[#121214]" data-testid="media-cover-badge">Cover</span>}
        <span className="absolute right-2 top-2 rounded-sm bg-black/70 px-2 py-1 text-xs uppercase text-zinc-200">{m.kind}</span>
      </div>
      <div className="space-y-2 p-3">
        {m.status !== "failed" && (
          <div className="flex gap-2">
            <Input placeholder="Add a caption" value={caption} onChange={(e) => setCaption(e.target.value)} className={`${inputCls} min-w-0 flex-1`} data-testid="media-caption-input" />
            <Button type="button" onClick={saveCaption} disabled={busy || caption === m.caption} className={btnSecondary} data-testid="media-caption-save-button">Save</Button>
          </div>
        )}
        <div className="flex gap-2">
          {m.status === "ready" && !isCover && (
            <Button type="button" onClick={() => onCover(m.id)} className={`${btnSecondary} flex-1`} data-testid="media-set-cover-button">
              <Star className="h-4 w-4" /> Set as cover
            </Button>
          )}
          <ConfirmDelete
            description={`This ${m.kind} will be removed from the website.`}
            onConfirm={remove}
            trigger={<Button type="button" className={`${btnSecondary} flex-1 text-[#ff8a80]`} data-testid="media-delete-button"><Trash2 className="h-4 w-4" /> Delete</Button>}
          />
        </div>
      </div>
    </li>
  );
};
